import type { BillingInterval, PlanId, SubscriptionStatus } from './types';
import { fechaLegible } from './cifras';
import { formatMoneyIn } from './currency';
import { planEnPausa } from './pausa';

/**
 * Planes y limites de uso.
 *
 * El asistente se paga por token, asi que es el unico coste variable del
 * producto: si no se acota, un tenant curioso puede costar mas de lo que paga.
 * Aqui vive la unica fuente de verdad de esos limites; el API los aplica y el
 * frontend los muestra.
 */

export interface PlanDefinition {
  id: PlanId;
  name: string;
  /** Precio mensual sin IVA */
  pricePerMonth: number;
  /** Mensajes al asistente incluidos al mes. null = sin limite */
  assistantMessagesPerMonth: number | null;
  /**
   * Escaneos de factura incluidos al mes (1 escaneo = 1 página). Desde el 15
   * oct 2026 todos los planes de pago lo traen; el módulo de ADDONS ya no
   * suma nada (ver `scanLimitFor`).
   */
  receiptScansPerMonth: number;
  maxLocations: number;
  maxRecipes: number | null;
  maxUsers: number;
  features: string[];
}

/** Días de prueba gratuita (los Founder no tienen: pagan desde el primer día). */
export const TRIAL_DAYS = 14;

/* ------------------------------------------------------------------ */
/* Periodo de cobro y oferta de lanzamiento (pricing de sep 2026)      */
/* ------------------------------------------------------------------ */

/**
 * Anual: se pagan 9 meses y se usan 12 (Alex, «Pricing 2026», 15 sep 2026).
 * El precio anual sale de aqui y del mensual; no es una cifra aparte que
 * pueda descuadrarse.
 */
export const ANNUAL_MONTHS_CHARGED = 9;

/**
 * Oferta de lanzamiento, SOLO en mensual: 50 % los tres primeros meses y
 * despues el precio normal. No se acumula con el anual: quien paga 9 meses
 * ya lleva su descuento. En Stripe es un cupon `repeating` de 3 meses que el
 * checkout aplica solo; aqui vive para que la landing, Ajustes y el MRR
 * cuenten lo mismo.
 */
export const LAUNCH_DISCOUNT = { percent: 50, months: 3 } as const;

/** Precio del año entero (9 meses), sin impuestos. */
export function pricePerYear(plan: PlanId): number {
  return (PLANS[plan]?.pricePerMonth ?? 0) * ANNUAL_MONTHS_CHARGED;
}

/** Lo que sale cada mes en anual (441/12 = 36,75), sin impuestos. */
export function annualEquivalentPerMonth(plan: PlanId): number {
  return Math.round((pricePerYear(plan) / 12) * 100) / 100;
}

/** Lo que se ahorra al año frente a doce meses sueltos (147 / 237). */
export function annualSavings(plan: PlanId): number {
  return (PLANS[plan]?.pricePerMonth ?? 0) * 12 - pricePerYear(plan);
}

/** Primeros meses de la oferta de lanzamiento (24,50 / 39,50). */
export function launchPricePerMonth(plan: PlanId): number {
  return Math.round((PLANS[plan]?.pricePerMonth ?? 0) * (1 - LAUNCH_DISCOUNT.percent / 100) * 100) / 100;
}

/** Cuanto aporta el plan al MRR segun el periodo: en anual, el año entre doce. */
export function planMrr(plan: PlanId, interval: BillingInterval = 'month'): number {
  return interval === 'year' ? annualEquivalentPerMonth(plan) : (PLANS[plan]?.pricePerMonth ?? 0);
}

/**
 * Los extras (escaner, usuario, local) van con el periodo del plan: Stripe no
 * deja mezclar intervalos en una suscripcion. Y en anual se pagan tambien 9
 * meses por 12 (Pol, 15 sep 2026): un solo mensaje, «en anual todo se paga
 * 9 meses». Esta es la unica conversion; el importe mensual es el de lista.
 */
export function extraPricePer(monthly: number, interval: BillingInterval = 'month'): number {
  return interval === 'year' ? monthly * ANNUAL_MONTHS_CHARGED : monthly;
}

/** Lo que aporta un extra al MRR: en anual, sus 9 meses entre doce. */
export function extraMrr(monthly: number, interval: BillingInterval = 'month'): number {
  return interval === 'year' ? Math.round(((monthly * ANNUAL_MONTHS_CHARGED) / 12) * 100) / 100 : monthly;
}

/**
 * Días que damos para arreglar un cobro fallido antes de tocar nada (mejora
 * 69, Alex, 8 sep 2026). Una tarjeta caducada o un banco que bloquea un cargo
 * no es un impago: es un trámite de dos minutos que el cliente todavía no
 * sabe que tiene pendiente. Durante estos días la cuenta funciona entera y se
 * avisa por correo y dentro de la app; después pasa a solo lectura, nunca se
 * borra nada. Ver `estadoDeImpago`.
 */
export const DIAS_GRACIA_IMPAGO = 5;

/** En qué punto del impago está una cuenta. */
export type EstadoImpago =
  /** Al corriente: ni hay cobro fallido, ni queda rastro de uno. */
  | 'al_dia'
  /** Cobro fallido, dentro de los días de gracia: acceso completo y aviso. */
  | 'gracia'
  /** Se agotaron los días: la cuenta entra en solo lectura. */
  | 'solo_lectura';

export interface ImpagoStatus {
  estado: EstadoImpago;
  /** Días completos que quedan de margen (0 cuando ya se agotó). */
  diasRestantes: number;
  /** Fin del margen en ISO, tal cual se guardó. */
  graceEndsAt?: string;
}

/**
 * Traduce el estado guardado en el tenant a lo que ve el usuario. Vive en
 * shared porque lo usan las dos capas: la API para cerrar la escritura y la
 * web para pintar el aviso, y tienen que contar exactamente lo mismo.
 *
 * `past_due` sin `graceEndsAt` son las cuentas anteriores a esta mejora: se
 * tratan como en gracia con el margen entero, que es lo justo — nunca han
 * recibido el aviso.
 */
export function estadoDeImpago(
  tenant: { subscriptionStatus: SubscriptionStatus; graceEndsAt?: string },
  ahora: number = Date.now(),
): ImpagoStatus {
  if (tenant.subscriptionStatus !== 'past_due') return { estado: 'al_dia', diasRestantes: 0 };
  const fin = tenant.graceEndsAt ? Date.parse(tenant.graceEndsAt) : NaN;
  if (!Number.isFinite(fin)) {
    return { estado: 'gracia', diasRestantes: DIAS_GRACIA_IMPAGO, graceEndsAt: tenant.graceEndsAt };
  }
  const restante = fin - ahora;
  if (restante <= 0) {
    return { estado: 'solo_lectura', diasRestantes: 0, graceEndsAt: tenant.graceEndsAt };
  }
  return {
    // Se redondea hacia arriba: con 30 h por delante quedan «2 días», no uno.
    estado: 'gracia',
    diasRestantes: Math.ceil(restante / 86_400_000),
    graceEndsAt: tenant.graceEndsAt,
  };
}

/** Fin del margen a partir del momento en que Stripe avisa del fallo. */
export function finDeGracia(desde: number = Date.now()): string {
  return new Date(desde + DIAS_GRACIA_IMPAGO * 86_400_000).toISOString();
}

/**
 * Tope de la cortesía donde el modelo pide un número (escaneos, usuarios,
 * locales): tan alto que en la práctica no existe. La interfaz no lo enseña.
 */
export const CORTESIA_SIN_LIMITE = 10_000;

/** ¿Es una cuenta de cortesía (todo gratis, sin Stripe)? */
export const esCortesia = (tenant: { plan?: PlanId } | null | undefined): boolean => tenant?.plan === 'cortesia';

/** Mensaje para cualquier intento de contratar algo en una cuenta de cortesía. */
export const CORTESIA_NADA_QUE_CONTRATAR =
  'Tu cuenta es de cortesía: ya lo tiene todo incluido y no hay nada que contratar.';

/**
 * Límites de la propuesta de precios del 15 oct 2026 (Pol y Alex, PDF
 * «Alexia - Precios propuesta 15 oct»). Los precios no cambian; cambia lo que
 * trae cada plan:
 *   - Básico: 150 escaneos y 150 mensajes, 1 usuario, 1 local. El escáner va
 *     INCLUIDO: el módulo de 9 al mes deja de venderse.
 *   - Pro: 500 escaneos y 500 mensajes, hasta 5 usuarios, 2 locales.
 *   - Prueba: todo lo de Pro con 50 escaneos y 50 mensajes.
 *   - Founder: 500 escaneos y 500 mensajes (Pol), sigue con 1 local.
 * Extras en cualquier plan: local extra (9, con 150 escaneos más al mes),
 * persona extra (5) y 150 escaneos más por 5, pago único solo para ese mes.
 */
export const PLANS: Record<PlanId, PlanDefinition> = {
  trial: {
    id: 'trial',
    name: 'Prueba',
    pricePerMonth: 0,
    assistantMessagesPerMonth: 50,
    // La prueba es Pro con menos escaneos y mensajes (Pol, 15 oct 2026): que
    // lo vean todo antes de pagar, sin que una prueba cueste como un cliente.
    receiptScansPerMonth: 50,
    maxLocations: 2,
    maxRecipes: 25,
    // La prueba da lo de Pro también en personas (revisión de Alex, 14 oct
    // 2026: «Personas 2 de 1» en prueba no tenía sentido).
    maxUsers: 5,
    features: ['Todo lo de Pro', '50 escaneos y 50 mensajes con Alexia', 'Sin tarjeta'],
  },
  basic: {
    id: 'basic',
    name: 'Básico',
    pricePerMonth: 49,
    assistantMessagesPerMonth: 150,
    receiptScansPerMonth: 150,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 1,
    features: [
      'Costeo: ingredientes, recetas y lo que te deja cada plato en cada canal',
      'Finanzas: ventas, gastos, estado de resultados y análisis',
      'Recuento mensual, proveedores, inversiones y objetivos',
      '150 escaneos al mes de facturas, tickets o notas (1 escaneo = 1 página)',
      '150 mensajes al mes con Alexia',
      '1 usuario y 1 local',
      'Soporte humano por WhatsApp',
    ],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    pricePerMonth: 79,
    assistantMessagesPerMonth: 500,
    receiptScansPerMonth: 500,
    maxLocations: 2,
    maxRecipes: null,
    maxUsers: 5,
    features: [
      'Todo lo del plan Básico',
      '500 escaneos al mes de facturas, tickets o notas',
      '500 mensajes al mes con Alexia',
      'Hasta 5 usuarios, cada uno con su rol',
      '2 locales incluidos',
      'Soporte humano por WhatsApp',
    ],
  },
  /**
   * Founder (cohorte fundadora, feedback 3 de Alex).
   *
   * 50 plazas, 39 €/mes con el precio BLOQUEADO mientras la suscripción siga
   * activa. Es Pro completo pero con 1 ubicación, y SIN prueba gratuita (se
   * paga desde el día 1). Desde el 15 oct 2026 trae 500 escaneos y 500
   * mensajes, como el Pro nuevo (Pol). Si cancelan y vuelven, entran al
   * precio vigente — el precio Founder no se recupera. La cohorte de 50 se
   * controla desde Stripe/negocio, no en el código.
   */
  founder: {
    id: 'founder',
    name: 'Founder',
    pricePerMonth: 39,
    assistantMessagesPerMonth: 500,
    receiptScansPerMonth: 500,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 5,
    features: [
      'Pro completo con 1 local incluido',
      'Precio de fundador bloqueado mientras sigas suscrito',
      '5 usuarios incluidos',
      '500 escaneos al mes (1 escaneo = 1 página)',
      '500 mensajes al mes con Alexia',
      'Acceso anticipado a nuevas funciones',
      'Onboarding directo y canal privado de feedback',
    ],
  },
  /**
   * Cortesía (Pol, 5 oct 2026): cuenta regalada desde /admin — la primera es
   * Casa Cortijo, el restaurante de Alex. Todo incluido y sin límites
   * prácticos, para siempre, sin Stripe: no hay suscripción, ni prueba, ni
   * cobros, y el webhook no la toca. Se marca también como interna, así que
   * no cuenta en MRR ni en clientes. No se contrata: solo la da un admin.
   */
  cortesia: {
    id: 'cortesia',
    name: 'Cortesía',
    pricePerMonth: 0,
    assistantMessagesPerMonth: null,
    receiptScansPerMonth: CORTESIA_SIN_LIMITE,
    maxLocations: CORTESIA_SIN_LIMITE,
    maxRecipes: null,
    maxUsers: CORTESIA_SIN_LIMITE,
    features: ['Todo incluido, sin límites', 'Sin cobros: cuenta de cortesía'],
  },
};

/**
 * Cohorte Founder CERRADA (Pol, 17 sep 2026). Nadie nuevo entra como Founder,
 * tenga el enlace o el código que tenga: la landing se retiró, la web ignora
 * la invitación y la API la rechaza. No cambia nada para quien ya es Founder
 * (conserva plan y precio bloqueado) ni para quien se dio de alta con la
 * invitación antes del cierre y aún no ha pagado (`founderPending`).
 * La API admite FOUNDER_COHORT_OPEN=true para reabrirla sin tocar código.
 */
export const FOUNDER_COHORT_OPEN = false;

export const FOUNDER_COHORT_CLOSED_MESSAGE =
  'La cohorte Founder ya está cerrada. Puedes empezar con la prueba gratuita de 14 días.';

/* ------------------------------------------------------------------ */
/* Ofertas por invitación                                              */
/* ------------------------------------------------------------------ */

/**
 * Oferta que llega por enlace (…/?invitacion=CODIGO) y no por un código
 * tecleado: el checkout mensual ya lleva el cupón de lanzamiento y Stripe no
 * deja cupón fijo y casilla de códigos en la misma sesión.
 *
 * ALEXFAMILY (Pol y Alex, 17 sep 2026): la comunidad de Alex contrata Pro al
 * precio de Básico durante un año, en mensual o en anual, si contrata antes
 * del 31 de octubre de 2026. Conserva la prueba de 14 días. NO se acumula con
 * la oferta de lanzamiento: la sustituye. Pasado el año, Stripe cobra Pro a
 * su precio sin que nadie haga nada.
 *
 * Los cupones tienen id fijo y los crea `infra/scripts/stripe-precios.mjs`,
 * limitados al producto Pro: en Básico no descuentan nada.
 */
export interface InvitationOffer {
  code: string;
  /** Cómo se le nombra al cliente. */
  name: string;
  plan: PlanId;
  /** Se cobra como este plan mientras dura. */
  pricedAs: PlanId;
  /** Meses que dura el precio rebajado desde que se contrata. */
  months: number;
  /** ISO. Último momento para contratar (fin del 31 oct en toda América). */
  redeemBy: string;
  /** Cupón mensual: `monthlyOff` cada mes durante `months` meses. */
  couponMonth: string;
  /** Cupón anual: `yearlyOff` de una vez, sobre el primer año. */
  couponYear: string;
}

export const INVITATION_QUERY_PARAM = 'invitacion';

export const INVITATION_OFFERS: Record<string, InvitationOffer> = {
  ALEXFAMILY: {
    code: 'ALEXFAMILY',
    name: 'Alex Family',
    plan: 'pro',
    pricedAs: 'basic',
    months: 12,
    redeemBy: '2026-11-01T08:00:00Z',
    couponMonth: 'alexia-alexfamily-mensual',
    couponYear: 'alexia-alexfamily-anual',
  },
};

/** La oferta de un código, si existe y aún se puede contratar. Sin distinguir mayúsculas. */
export function invitationOffer(code: string | undefined | null, now: number = Date.now()): InvitationOffer | null {
  const offer = INVITATION_OFFERS[(code ?? '').trim().toUpperCase()];
  if (!offer || now >= Date.parse(offer.redeemBy)) return null;
  return offer;
}

/** Lo que descuenta la oferta en cada factura del periodo (30 al mes · 270 el año). */
export function offerDiscount(offer: InvitationOffer, interval: BillingInterval = 'month'): number {
  return interval === 'year'
    ? pricePerYear(offer.plan) - pricePerYear(offer.pricedAs)
    : (PLANS[offer.plan]?.pricePerMonth ?? 0) - (PLANS[offer.pricedAs]?.pricePerMonth ?? 0);
}

/** Cupón de Stripe de la oferta para ese periodo. */
export function offerCoupon(offer: InvitationOffer, interval: BillingInterval = 'month'): string {
  return interval === 'year' ? offer.couponYear : offer.couponMonth;
}

/**
 * ¿Sigue pagando el precio rebajado? Solo en el plan de la oferta y durante
 * sus meses: si baja a Básico el cupón no aplica, y pasado el año caduca.
 * No mira `redeemBy`: eso es el plazo para contratar, no lo que dura.
 */
export function ofertaVigente(
  tenant: { plan: PlanId; offer?: { code: string; startedAt: string } },
  now: number = Date.now(),
): InvitationOffer | null {
  const offer = tenant.offer ? INVITATION_OFFERS[tenant.offer.code] : undefined;
  if (!offer || tenant.plan !== offer.plan) return null;
  const fin = new Date(tenant.offer!.startedAt);
  fin.setUTCMonth(fin.getUTCMonth() + offer.months);
  return now < fin.getTime() ? offer : null;
}

/**
 * ¿El plan incluye el escáner de facturas de serie? Desde el 15 oct 2026, todos
 * los planes de pago (y la prueba y la cortesía) lo traen.
 */
export function scannerIncluido(plan: PlanId): boolean {
  return (PLANS[plan]?.receiptScansPerMonth ?? 0) > 0;
}

/**
 * Lo que un tenant aporta al MRR: plan + escaner suelto + asientos y centros
 * extra. Cuenta tambien a los `past_due` —siguen suscritos y Stripe los sigue
 * intentando cobrar—; solo salen del MRR al cancelar.
 *
 * Es una estimacion desde nuestros datos (los webhooks de Stripe): la cifra
 * exacta, con cupones y prorrateos, la da siempre Stripe.
 */
export function mrrDeTenant(tenant: {
  plan: PlanId;
  subscriptionStatus: string;
  addons?: readonly AddonId[];
  extraSeats?: number;
  extraLocations?: number;
  founderPending?: boolean;
  billingInterval?: BillingInterval;
  offer?: { code: string; startedAt: string };
  pausedFrom?: string;
  pausedUntil?: string;
}, ahora: number = Date.now()): number {
  // Un Founder que aun no ha pagado no es MRR, diga lo que diga el estado
  // de su suscripcion (un checkout con la tarjeta rechazada queda past_due).
  if (tenant.founderPending) return 0;
  // En pausa (retención, 18 oct 2026) Stripe sigue en `active`, pero no cobra
  // nada hasta `pausedUntil`: mientras dure, no suma MRR.
  if (planEnPausa(tenant, ahora)) return 0;
  if (tenant.subscriptionStatus !== 'active' && tenant.subscriptionStatus !== 'past_due') {
    return 0;
  }
  // Anual: 441 al año son 36,75 al mes, y los extras tambien van a 9 por 12.
  const periodo = tenant.billingInterval;
  // Con una oferta por invitación viva se cobra como el plan de abajo (Pro a 49).
  const oferta = ofertaVigente(tenant, ahora);
  const plan = planMrr(oferta ? oferta.pricedAs : tenant.plan, periodo);
  const escaner = (tenant.addons ?? []).includes('escaner')
    ? extraMrr(ADDONS.escaner.pricePerMonth, periodo)
    : 0;
  const asientos = Math.max(0, tenant.extraSeats ?? 0) * extraMrr(EXTRA_SEAT_PRICE_PER_MONTH, periodo);
  const centros =
    Math.max(0, tenant.extraLocations ?? 0) * extraMrr(EXTRA_LOCATION_PRICE_PER_MONTH, periodo);
  return Math.round((plan + escaner + asientos + centros) * 100) / 100;
}

/* ------------------------------------------------------------------ */
/* Coste del asistente                                                 */
/* ------------------------------------------------------------------ */

/**
 * Coste por millon de tokens del modelo del asistente, en euros.
 *
 * Es una estimacion para poder vigilar el margen sin salir de la app: la cifra
 * buena es siempre la factura de AWS. Se sobrescribe por variable de entorno
 * cuando cambien los precios de Bedrock, para no tener que desplegar codigo.
 */
export interface ModelPricing {
  inputPerMillion: number;
  outputPerMillion: number;
}

export const DEFAULT_MODEL_PRICING: ModelPricing = {
  inputPerMillion: 3,
  outputPerMillion: 15,
};

export interface AssistantUsage {
  whatsappCost?: number;
  transcribeCost?: number;
  textractCost?: number;
  tenantId: string;
  /** YYYY-MM */
  month: string;
  messages: number;
  /** Facturas escaneadas este mes. Cuota aparte de la del asistente. */
  scans: number;
  /**
   * Escaneos comprados este mes con el paquete de pago único (SCAN_PACK).
   * Solo valen para este mes: el mes siguiente es otro USAGE# y empieza en 0.
   */
  extraScans?: number;
  inputTokens: number;
  outputTokens: number;
  /** Coste estimado de las llamadas de texto a Bedrock, en euros. */
  bedrockCost?: number;
  /** Coste estimado de las transcripciones de OpenAI, en euros. */
  openAiCost?: number;
  /** Segundos de audio enviados a transcripción. */
  audioSeconds?: number;
  /** Grabaciones transcritas correctamente. */
  voiceRecipes?: number;
  /** Coste estimado acumulado del mes, en euros */
  estimatedCost: number;
  updatedAt: string;
}

export interface UsageStatus {
  month: string;
  plan: PlanId;
  planName: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  /** Primer dia del mes siguiente, cuando se reinicia el contador */
  resetsOn: string;
  estimatedCost: number;
  /** true si ya no puede preguntar mas este mes */
  exhausted: boolean;
  /** true a partir del 80 % del limite, para avisar antes de cortar */
  nearLimit: boolean;
}

export function estimateCost(
  inputTokens: number,
  outputTokens: number,
  pricing: ModelPricing = DEFAULT_MODEL_PRICING,
): number {
  const cost =
    (inputTokens / 1_000_000) * pricing.inputPerMillion +
    (outputTokens / 1_000_000) * pricing.outputPerMillion;
  return Math.round(cost * 10_000) / 10_000;
}

export function nextMonthStart(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

export function usageStatus(
  plan: PlanId,
  usage: Pick<AssistantUsage, 'month' | 'messages' | 'estimatedCost'>,
): UsageStatus {
  const definition = PLANS[plan] ?? PLANS.trial;
  const limit = definition.assistantMessagesPerMonth;
  const used = usage.messages;
  const remaining = limit == null ? null : Math.max(0, limit - used);

  return {
    month: usage.month,
    plan: definition.id,
    planName: definition.name,
    used,
    limit,
    remaining,
    resetsOn: nextMonthStart(usage.month),
    estimatedCost: usage.estimatedCost,
    exhausted: limit != null && used >= limit,
    nearLimit: limit != null && used >= limit * 0.8,
  };
}

/** Mensaje que ve el duenyo cuando agota su cuota. Sin jerga ni culpa. */
export function quotaMessage(status: UsageStatus): string {
  // Fechas como en la app (14 oct 2026): «1 nov».
  const fecha = fechaLegible(status.resetsOn, status.resetsOn);
  return (
    `Has usado los ${status.limit} mensajes con Alexia que incluye tu plan ${status.planName} este mes. ` +
    `El contador se reinicia el ${fecha}. Si te has quedado corto, puedes subir de plan desde Configuración.`
  );
}

/* ------------------------------------------------------------------ */
/* Modulos de pago                                                     */
/* ------------------------------------------------------------------ */

export type AddonId = 'escaner';

export interface AddonDefinition {
  id: AddonId;
  name: string;
  /** Precio mensual sin IVA, en euros. */
  pricePerMonth: number;
  /** Escaneos incluidos al mes. */
  scansPerMonth: number;
  description: string;
}

/**
 * Escaner de facturas como módulo suelto (RETIRADO el 15 oct 2026).
 *
 * Se vendía aparte en Básico por 9 al mes con 150 escaneos. Con los precios
 * del 15 oct el Básico ya trae 150 escaneos incluidos, así que el módulo deja
 * de venderse: no se puede contratar, y a quien lo paga se le quita al final
 * del periodo pagado, sin devolución y con aviso
 * (infra/scripts/quitar-modulo-escaner.mjs, docs/product/aviso-modulo-escaner.md).
 *
 * Mientras tanto la línea sigue en Stripe y cuenta en el MRR (es dinero que
 * entra), pero NO suma escaneos: ver `scanLimitFor`.
 */
export const ADDONS: Record<AddonId, AddonDefinition> = {
  escaner: {
    id: 'escaner',
    name: 'Escáner de facturas',
    pricePerMonth: 9,
    scansPerMonth: 150,
    description:
      'Sube una foto o PDF de una factura y Alexia extraerá los datos para proponerte el gasto: proveedor, fecha, importe sin IVA, IVA y total. Tú solo tienes que revisarlo y confirmar. Cada página cuenta como un escaneo.',
  },
};

/* ------------------------------------------------------------------ */
/* Asientos                                                            */
/* ------------------------------------------------------------------ */

/**
 * Asiento extra: 5 €/mes por usuario por encima de los que incluye el plan
 * (Basico 1 · Pro 5). No es un AddonId porque lleva cantidad — se contratan
 * N asientos, no un modulo que esta o no esta. En Stripe es una linea propia
 * cuya `quantity` acaba en `Tenant.extraSeats` via webhook.
 *
 * Decision de precio (Alex, 21 ago 2026): Basico se queda en 1 usuario a
 * proposito, pero el asiento extra es barato para que compartir contraseña
 * no compense — compartirla mata la trazabilidad de quien metio cada dato.
 */
export const EXTRA_SEAT_PRICE_PER_MONTH = 5;

/** Centro extra: 9 €/mes por cada centro por encima del incluido. */
export const EXTRA_LOCATION_PRICE_PER_MONTH = 9;

/** Asientos totales de un tenant: los del plan mas los contratados. */
export function seatLimitFor(plan: PlanId, extraSeats = 0): number {
  const delPlan = PLANS[plan]?.maxUsers ?? 1;
  return delPlan + Math.max(0, Math.floor(extraSeats));
}

/** Centros totales de un tenant: los del plan (Pro trae 2 desde el 15 oct 2026) más los contratados. */
export function locationLimitFor(plan: PlanId, extraLocations = 0): number {
  const delPlan = PLANS[plan]?.maxLocations ?? 1;
  return delPlan + Math.max(0, Math.floor(extraLocations));
}

/**
 * Limite de centros de un tenant: el del plan, mas los contratados en Stripe,
 * mas los de cortesia regalados desde /admin (7 sep 2026). Es lo que deben
 * usar todas las comprobaciones; `locationLimitFor` a secas se queda para
 * simular una cantidad contratada distinta.
 */
export function locationLimitOf(
  tenant: { plan: PlanId; extraLocations?: number; bonusLocations?: number },
  extraLocations = tenant.extraLocations ?? 0,
): number {
  return locationLimitFor(tenant.plan, extraLocations + Math.max(0, Math.floor(tenant.bonusLocations ?? 0)));
}

/* ------------------------------------------------------------------ */
/* Escaneos (precios del 15 oct 2026)                                  */
/* ------------------------------------------------------------------ */

/** Cada local extra contratado trae 150 escaneos más al mes. */
export const SCANS_PER_EXTRA_LOCATION = 150;

/**
 * «150 escaneos más · 5»: pago único, solo para el mes en que se compra, no se
 * renueva. En Stripe es un Price sin `recurring` que se cobra con un Checkout
 * en modo `payment`; el webhook suma `scans` a `extraScans` del uso de ESE mes
 * (USAGE#YYYY-MM), una sola vez por sesión de pago. Misma cifra en $ y €.
 */
export const SCAN_PACK = { scans: 150, price: 5 } as const;

/** Lo que sube el límite de escaneos de un tenant además de su plan. */
export interface ScanExtras {
  /** Locales extra contratados en Stripe (los regalados desde /admin no suman). */
  extraLocations?: number;
  /** Escaneos comprados este mes con el paquete de pago único. */
  extraScans?: number;
}

/**
 * Escaneos al mes de un tenant: los del plan, más 150 por local extra, más
 * los paquetes comprados ese mes.
 *
 * El módulo de escáner antiguo (ADDONS.escaner) YA NO SUMA: quien lo paga en
 * Básico tiene los mismos 150 que cualquier Básico (no 300) mientras se le
 * acaba el periodo pagado (Pol, 15 oct 2026). Por eso esta función no recibe
 * los addons.
 */
export function scanLimitFor(plan: PlanId, extras: ScanExtras = {}): number {
  const delPlan = PLANS[plan]?.receiptScansPerMonth ?? 0;
  if (plan === 'cortesia' || delPlan <= 0) return delPlan;
  const locales = Math.max(0, Math.floor(extras.extraLocations ?? 0)) * SCANS_PER_EXTRA_LOCATION;
  const comprados = Math.max(0, Math.floor(extras.extraScans ?? 0));
  return delPlan + locales + comprados;
}

/** ¿Tiene derecho a escanear? (aunque haya agotado la cuota del mes) */
export const puedeEscanear = (plan: PlanId): boolean => scanLimitFor(plan) > 0;

/**
 * ¿Puede comprar el paquete de 150 escaneos? Solo quien ya paga un plan: en la
 * prueba no hay tarjeta, y lo que toca es elegir plan. La cortesía no compra.
 */
export function puedeComprarEscaneos(tenant: { plan: PlanId; subscriptionStatus?: SubscriptionStatus | string; founderPending?: boolean }): boolean {
  if (tenant.founderPending) return false;
  if (tenant.plan !== 'basic' && tenant.plan !== 'pro' && tenant.plan !== 'founder') return false;
  return tenant.subscriptionStatus === 'active' || tenant.subscriptionStatus === 'past_due';
}

/**
 * Lo que se le dice a quien quiere las facturas por correo sin tener escáner.
 * Desde el 15 oct 2026 todos los planes lo traen, así que solo lo ve una
 * cuenta sin plan reconocido.
 */
export function inboundEmailSinEscanerMessage(planName: string): string {
  return (
    `Las facturas por correo usan el escáner de facturas, y el plan ${planName} no lo incluye. ` +
    'Elige el plan Básico o Pro desde Configuración → Tu plan: los dos lo traen.'
  );
}

export interface ScanQuota {
  used: number;
  limit: number;
  remaining: number;
  exhausted: boolean;
  nearLimit: boolean;
  /** true = el plan no trae escáner (ya no pasa en ningún plan de pago). */
  sinModulo: boolean;
  resetsOn: string;
  /** Desglose del límite, para «Lo que llevas este mes». */
  planScans: number;
  /** 150 por cada local extra contratado. */
  locationScans: number;
  /** Comprados este mes con el paquete de pago único. */
  extraScans: number;
}

export function scanQuota(
  tenant: { plan: PlanId; extraLocations?: number },
  used: number,
  month: string,
  extraScans = 0,
): ScanQuota {
  const limit = scanLimitFor(tenant.plan, { extraLocations: tenant.extraLocations, extraScans });
  const planScans = PLANS[tenant.plan]?.receiptScansPerMonth ?? 0;
  const conPlan = planScans > 0 && tenant.plan !== 'cortesia';
  return {
    used,
    limit,
    remaining: Math.max(0, limit - used),
    exhausted: limit > 0 && used >= limit,
    nearLimit: limit > 0 && used >= limit * 0.8,
    sinModulo: limit === 0,
    resetsOn: nextMonthStart(month),
    planScans,
    locationScans: conPlan ? Math.max(0, Math.floor(tenant.extraLocations ?? 0)) * SCANS_PER_EXTRA_LOCATION : 0,
    extraScans: conPlan ? Math.max(0, Math.floor(extraScans)) : 0,
  };
}

/**
 * «Te quedan N escaneos este mes» solo al llegar al 80 % del plan (revisión de
 * Alex del 14 oct 2026: antes salía siempre que quedaban menos de 20). Nunca
 * en cortesía, sin módulo o ya agotado (ahí sale el mensaje de bloqueo).
 */
export function avisarEscaneosQueQuedan(quota: Pick<ScanQuota, 'used' | 'limit'> & { exhausted?: boolean; sinModulo?: boolean }): boolean {
  if (!(quota.limit > 0) || quota.limit >= CORTESIA_SIN_LIMITE || quota.sinModulo || quota.exhausted) return false;
  return quota.used >= quota.limit * 0.8 && quota.used < quota.limit;
}

/** «$5.00» o «5,00 €» según la moneda en la que se cobra. */
export function precioPaqueteEscaneos(moneda: 'EUR' | 'USD' = 'EUR'): string {
  // Como el resto de precios (PlanPicker): «$5.00» y «5,00 €», no «5 $» (bugbot 15 oct 2026).
  return formatMoneyIn(SCAN_PACK.price, moneda);
}

/** Lo que se le dice a quien no puede escanear. Sin jerga y con salida. */
export function scanQuotaMessage(
  quota: ScanQuota,
  planName: string,
  opciones: { puedeComprar?: boolean; moneda?: 'EUR' | 'USD' } = {},
): string {
  if (quota.sinModulo) {
    return (
      `El plan ${planName} no incluye el escáner de facturas. ` +
      `Elige el plan Básico (${PLANS.basic.receiptScansPerMonth} escaneos al mes) o Pro ` +
      `(${PLANS.pro.receiptScansPerMonth}) desde Configuración → Tu plan.`
    );
  }
  const fecha = fechaLegible(quota.resetsOn, quota.resetsOn);
  const salida = opciones.puedeComprar
    ? `Si necesitas más, compra ${SCAN_PACK.scans} escaneos por ${precioPaqueteEscaneos(opciones.moneda)} desde Configuración → Tu plan (solo para este mes). `
    : '';
  return (
    `Has usado los ${quota.limit} escaneos de este mes (cada página cuenta como uno). El contador se reinicia el ${fecha}. ` +
    salida +
    'Mientras tanto puedes seguir registrando gastos manualmente.'
  );
}
